import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, ShoppingCart, Loader2, X, Trash2, PackageCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { PO_STATUSES, PO_NEXT } from "./procurementConstants";
import { formatCurrency, formatDateTime } from "./inventoryConstants";
import { ReceiveDialog } from "./ReceiveDialog";

const emptyLine = () => ({ part_id: "", part_number: "", description: "", quantity: 1, unit_cost: 0, core_charge: 0 });

const POFormModal = ({ open, onClose, vendors, parts, currentUser, onSaved }) => {
  const [vendorId, setVendorId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  useEffect(() => { if (open) { setVendorId(""); setNotes(""); setLines([emptyLine()]); } }, [open]);
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const onPart = (i, id) => { const p = parts.find((x) => x.id === id); setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, part_id: id, part_number: p?.part_number || "", description: l.description || p?.name || "", unit_cost: l.unit_cost || p?.unit_cost || 0 } : l)); };

  const submit = async () => {
    if (!vendorId) { toast.error("Select a vendor"); return; }
    const valid = lines.filter((l) => l.description?.trim());
    if (valid.length === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      await inventoryApi.createPurchaseOrder({ vendor_id: vendorId, notes, created_by: userName,
        line_items: valid.map((l) => ({ part_id: l.part_id || null, part_number: l.part_number || null, description: l.description, quantity: Number(l.quantity) || 1, unit_cost: Number(l.unit_cost) || 0, core_charge: Number(l.core_charge) || 0 })) });
      toast.success("Purchase order created"); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="po-form-modal">
        <DialogTitle className="sr-only">New Purchase Order</DialogTitle>
        <DialogDescription className="sr-only">Create a purchase order</DialogDescription>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">New Purchase Order</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div><Label className="text-xs text-slate-500 mb-1 block">Vendor</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="po-vendor-select">
              <option value="">Select vendor...</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label className="text-sm font-semibold text-slate-800">Line Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button></div>
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`po-line-${i}`}>
                  <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Line {i + 1}</span>
                    {lines.length > 1 && <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</div>
                  <select className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm" value={l.part_id} onChange={(e) => onPart(i, e.target.value)}>
                    <option value="">Link inventory part (optional)...</option>
                    {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} — {p.name}</option>)}
                  </select>
                  <Input placeholder="Description *" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`po-line-desc-${i}`} />
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px] text-slate-400">Qty</Label><Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></div>
                    <div><Label className="text-[10px] text-slate-400">Unit Cost</Label><Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} /></div>
                    <div><Label className="text-[10px] text-slate-400">Core</Label><Input type="number" step="0.01" value={l.core_charge} onChange={(e) => setLine(i, "core_charge", e.target.value)} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" onClick={submit} disabled={saving} data-testid="po-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create PO"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PODetailModal = ({ poId, onClose, onChanged, canManage, vehicles, vendors, currentUser, onReceive }) => {
  const [po, setPo] = useState(null);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";
  const load = () => { if (poId) inventoryApi.getPurchaseOrder(poId).then(setPo).catch(() => {}); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [poId]);
  if (!poId || !po) return null;
  const st = PO_STATUSES[po.status] || {};
  const nexts = PO_NEXT[po.status] || [];
  const changeStatus = async (s) => { try { await inventoryApi.updatePurchaseOrder(po.id, { status: s, user: userName }); toast.success(`Marked ${PO_STATUSES[s]?.label || s}`); load(); onChanged(); } catch { toast.error("Failed"); } };
  const canReceive = ["ordered", "submitted", "partially_received", "draft"].includes(po.status);

  return (
    <Dialog open={!!poId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="po-detail-modal">
        <DialogTitle className="sr-only">{po.po_number}</DialogTitle>
        <DialogDescription className="sr-only">Purchase order details</DialogDescription>
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2"><h2 className="text-lg font-semibold text-slate-900">{po.po_number}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color}`}>{st.label}</span></div>
            <div className="text-sm text-slate-500 mt-0.5">{po.vendor_name} · {formatDateTime(po.created_at)}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {po.line_items.map((l) => (
            <div key={l.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-3">
              <div className="min-w-0"><div className="text-sm font-medium text-slate-900 truncate">{l.description}</div>
                <div className="text-xs text-slate-500">{l.part_number ? `${l.part_number} · ` : ""}Qty {l.quantity} · {formatCurrency(l.unit_cost)}{l.core_charge ? ` · Core ${formatCurrency(l.core_charge)}` : ""}</div></div>
              <div className="text-right"><div className="text-sm font-semibold">{formatCurrency((l.unit_cost || 0) * (l.quantity || 0))}</div>
                {l.qty_received > 0 && <div className="text-[10px] text-green-600">Recv {l.qty_received}/{l.quantity}</div>}</div>
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-slate-100 text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{formatCurrency(po.subtotal)}</span></div>
          {po.core_total > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Cores</span><span>{formatCurrency(po.core_total)}</span></div>}
        </div>
        {canManage && (
          <div className="px-5 py-4 border-t border-slate-200 flex-shrink-0 flex flex-wrap gap-2">
            {canReceive && <Button onClick={() => onReceive(po)} data-testid="po-receive-btn"><PackageCheck className="w-4 h-4 mr-1" /> Receive Parts</Button>}
            {nexts.map((s) => <Button key={s} size="sm" variant="outline" onClick={() => changeStatus(s)} data-testid={`po-status-${s}-btn`}>{PO_STATUSES[s]?.label || s} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PurchaseOrdersSection = ({ currentUser, canManage, vehicles }) => {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [vendors, setVendors] = useState([]);
  const [parts, setParts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [receivePo, setReceivePo] = useState(null);

  const fetchPos = useCallback(async () => {
    setLoading(true);
    try { const params = {}; if (statusFilter) params.status = statusFilter; setPos(await inventoryApi.getPurchaseOrders(params)); }
    catch { toast.error("Failed to load POs"); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { inventoryApi.getVendors().then(setVendors).catch(() => {}); inventoryApi.getParts().then(setParts).catch(() => {}); }, []);
  useEffect(() => { fetchPos(); }, [fetchPos]);

  return (
    <div className="space-y-4" data-testid="purchase-orders-section">
      <div className="flex items-center justify-between">
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="po-status-filter">
          <option value="">All Statuses</option>
          {Object.entries(PO_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {canManage && <Button onClick={() => setShowForm(true)} data-testid="new-po-btn"><Plus className="w-4 h-4 mr-1" /> New PO</Button>}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : pos.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No purchase orders</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {pos.map((p) => {
            const st = PO_STATUSES[p.status] || {};
            return (
              <button key={p.id} onClick={() => setDetailId(p.id)} data-testid={`po-card-${p.id}`} className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-blue-200 transition-all">
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{p.po_number}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color}`}>{st.label}</span></div>
                <div className="text-sm text-slate-500 mt-1">{p.vendor_name}</div>
                <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                  <span>{p.line_items?.length || 0} line(s)</span>
                  <span className="text-sm font-semibold text-slate-700">{formatCurrency(p.subtotal)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <POFormModal open={showForm} onClose={() => setShowForm(false)} vendors={vendors} parts={parts} currentUser={currentUser} onSaved={fetchPos} />
      <PODetailModal poId={detailId} onClose={() => setDetailId(null)} onChanged={fetchPos} canManage={canManage} vehicles={vehicles} vendors={vendors} currentUser={currentUser} onReceive={(po) => { setDetailId(null); setReceivePo(po); }} />
      <ReceiveDialog open={!!receivePo} onClose={() => setReceivePo(null)} po={receivePo} vendors={vendors} vehicles={vehicles} currentUser={currentUser} onReceived={fetchPos} />
    </div>
  );
};
